import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatINR } from "../lib/format";
import type { CreditLimitView } from "../lib/types";
import { Panel, ProgressBar } from "./ui";

/**
 * What is left on each credit limit today. A limit a bank shares across your
 * cards with it is one row: spend on any of them uses it up.
 */
export function CreditLimits({ className = "" }: { className?: string }) {
  const [limits, setLimits] = useState<CreditLimitView[] | null>(null);

  useEffect(() => {
    api
      .get<{ limits: CreditLimitView[] }>("/cards/credit")
      .then((r) => setLimits(r.limits))
      .catch(() => setLimits([]));
  }, []);

  if (!limits || limits.length === 0) return null;
  return (
    <Panel
      className={className}
      title="Available credit"
      subtitle="Unbilled spend, plus the last statement until its due date — after that it counts as paid"
    >
      <ul className="space-y-4">
        {limits.map((l) => (
          <li key={l.key}>
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 text-sm font-medium text-ink-900">
                {l.shared ? `${l.issuer} · shared limit` : l.cards[0].name}
                {l.shared && <span className="block text-xs font-normal text-ink-500">{l.cards.map((c) => c.name).join(" + ")}</span>}
              </p>
              <span className="tnum shrink-0 text-xs text-ink-500">
                {formatINR(l.used)} / {formatINR(l.limit, { compact: l.limit >= 100000 })}
              </span>
            </div>
            <div className="mt-2">
              <ProgressBar pct={l.pct} color={l.pct >= 90 ? "#b54708" : l.cards[0].color} />
            </div>
            <p className={`mt-1.5 text-xs ${l.available < 0 ? "text-amber-800" : "text-ink-600"}`}>
              <span className="font-medium text-ink-900">{formatINR(l.available)}</span> available
              {l.shared && l.byCard.some((c) => c.owed > 0) && (
                <span className="text-ink-500">
                  {" · "}
                  {l.byCard
                    .filter((c) => c.owed > 0)
                    .map((c) => `${c.name} ${formatINR(c.owed)}`)
                    .join(", ")}
                </span>
              )}
            </p>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
